/** Rótulo humano da origem de uma alteração de custo. */
export const ORIGIN_LABELS: Record<string, string> = {
  MANUAL: "Manual",
  XLSX: "Importação XLSX",
  NF_FORNECEDOR: "NF do fornecedor",
};

/** Campos operacionais do histórico de custo de produto (o restante pertence à matéria-prima). */
export const OPERATIONAL_COST_FIELDS = new Set<string>([
  "Modalidade operacional",
  "Valor operacional",
  "Percentual operacional",
  "Unidade operacional",
  "Observação (operacional)",
]);
const INITIAL_COST_FIELD = "Cadastro inicial";

/**
 * Classificação do histórico de custo de produto por componente, para que o relógio de cada componente
 * mostre SOMENTE as suas alterações: uma edição do operacional nunca aparece como matéria-prima. O cadastro
 * inicial (cria os dois) aparece em ambos.
 */
export const isOperationalChange = (changedFields: string[]): boolean =>
  changedFields.some((f) => OPERATIONAL_COST_FIELDS.has(f)) || changedFields.includes(INITIAL_COST_FIELD);
export const isRawChange = (changedFields: string[]): boolean =>
  changedFields.length === 0 || changedFields.some((f) => !OPERATIONAL_COST_FIELDS.has(f));

/** Entrada já NORMALIZADA de histórico de um componente (o chamador filtra/mapeia a sua fonte). */
export interface CostHistoryView {
  id: string;
  changedAt: string;
  changedBy: string;
  /** "MANUAL" | "XLSX" | "NF_FORNECEDOR" (ou vazio = sempre manual, p.ex. composição). */
  origin: string;
  oldValues: string;
  newValues: string;
  unitName?: string | null;
  note?: string | null;
  importId?: string | null;
  costApplicationId?: string | null;
  /** Nome do emitente DECLARADO no XML da NF (origem NF_FORNECEDOR); null em registros antigos/sem backfill. */
  nfSupplierName?: string | null;
  /** Chave de acesso da NF-e para abrir o DANFE gerado do XML (origem NF_FORNECEDOR); null nas demais. */
  nfAccessKey?: string | null;
  /** Número da NF (origem NF_FORNECEDOR); null nas demais. */
  nfNumber?: string | null;
}

/**
 * Referência exibível da alteração: importação XLSX, NF do fornecedor (número + nome do emitente registrado
 * no XML), ou "Referência não registrada" para alterações manuais e registros antigos — NUNCA inventa
 * origem/valor/fornecedor.
 */
export function referenceLabel(entry: CostHistoryView): string {
  if (entry.origin === "XLSX" && entry.importId) return "Importação XLSX";
  if (entry.origin === "NF_FORNECEDOR" && entry.costApplicationId) {
    const parts: string[] = [];
    if (entry.nfNumber?.trim()) parts.push(`NF nº ${entry.nfNumber.trim()}`);
    if (entry.nfSupplierName?.trim()) parts.push(entry.nfSupplierName.trim());
    if (parts.length) return parts.join(" — ");
    return entry.note?.trim() ? entry.note.trim() : "NF do fornecedor";
  }
  return "Referência não registrada";
}

/**
 * Chave de acesso para abrir o "DANFE gerado do XML" quando a referência da NF é abrível — origem
 * NF_FORNECEDOR com aplicação de custo vinculada E chave resolvida. Null (não clicável) em registros antigos
 * sem vínculo ou quando a NF não tem chave disponível: nunca cria link inválido.
 */
export function nfDanfeAccessKey(entry: CostHistoryView): string | null {
  if (entry.origin === "NF_FORNECEDOR" && entry.costApplicationId && entry.nfAccessKey?.trim()) {
    return entry.nfAccessKey.trim();
  }
  return null;
}
